import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { BerthSide, EquipmentDto } from '@operations/app/api'
import { EquipmentStatusChip } from '@operations/features/equipments/components/statusChip/EquipmentStatus'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog, WarningIcon } from '@tom-ui/ui'
import { useEffect, useState } from 'react'

interface Props {
  selectedCranes: number[]
  craneOptions: EquipmentDto[]
  equipmentIdsInUse: number[]
  berths: string[]
  remarks?: string
  side?: BerthSide
  isOpen: boolean
  hasWorkInstructions: boolean
  existingCraneSplitBerthSide: BerthSide | null | undefined
  onPlan: (cranes: number[]) => Promise<void> | void
  onClose: () => Promise<void> | void
}

export const SelectCranes = ({
  selectedCranes,
  craneOptions,
  equipmentIdsInUse,
  berths,
  remarks,
  side,
  isOpen,
  hasWorkInstructions,
  existingCraneSplitBerthSide,
  onPlan,
  onClose,
}: Props) => {
  const { t } = useTranslate()
  const [cranes, setCranes] = useState<number[]>(selectedCranes.filter(c => c !== 0))
  const handleCraneChange = (event: SelectChangeEvent<number[]>) => {
    setCranes(event.target.value as number[])
  }

  const handleSubmit = () => {
    onPlan(cranes)
  }

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    setCranes(selectedCranes.filter(c => c !== 0))
  }, [selectedCranes, setCranes])

  return (
    <ConfirmationDialog
      open={isOpen}
      title={
        existingCraneSplitBerthSide
          ? t('editCraneSplitQuestion', 'Edit crane split?')
          : t('createCraneSplitQuestion', 'Create crane split?')
      }
      primaryActionText={
        existingCraneSplitBerthSide ? t('yesEdit', 'Yes, edit') : t('yesCreate', 'Yes, create')
      }
      primaryActionDataCy={'confirm-crane-split-creation-btn'}
      closeLabel={t('noCancel', 'No, cancel')}
      primaryActionDisabled={!cranes.length || !side}
      maxWidth='xs'
      onClose={handleClose}
      onConfirm={handleSubmit}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <Typography variant='body1' textAlign='left' color='secondary'>
          {hasWorkInstructions
            ? side && side !== BerthSide.Unspecified
              ? existingCraneSplitBerthSide === side || !existingCraneSplitBerthSide
                ? t(
                    'craneSplitAdditionalInformation',
                    'In order to automatically plan the crane split, we need some additional information from you',
                  )
                : t(
                    'berthSideGotUpdatedYouNeedToRedoCraneSplit',
                    'berth side got updated, you need to redo the crane split',
                  ) +
                  `
                    ${existingCraneSplitBerthSide} => ${side}`
              : t(
                  'pleaseSpecifyBirthSideBeforePlanOperations',
                  'Please specify the Berth Side before plan operations',
                )
            : t(
                'pleaseInsertTheBapliFileBefore',
                'Please insert the baplie file before planning the operations',
              )}
        </Typography>
        {!side && (
          <Box>
            <Stack direction='row' alignItems='center' gap={1}>
              <WarningIcon fontSize='small' color='warning' />
              <Typography variant='body2' color='#7A4F01'>
                {t('pleaseSelectTheBerthSide', 'Please select the berth side to the crane split')}
              </Typography>
            </Stack>
          </Box>
        )}

        {!!remarks && (
          <Box>
            <Typography variant='subtitle1' paddingBottom='.5rem'>
              {t('remarks', 'Remarks')}
            </Typography>
            <TextField
              label={t('remarks', 'Remarks')}
              value={remarks}
              multiline
              rows={3}
              disabled
              fullWidth
            />
          </Box>
        )}
        <Box>
          <Typography variant='subtitle1' paddingBottom='.5rem'>
            {t('crane(s)', 'Crane(s)')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{t('crane', 'Crane')}</InputLabel>
            <Select
              label={t('crane', 'Crane')}
              value={cranes}
              onChange={handleCraneChange}
              disabled={!hasWorkInstructions || !side || side === BerthSide.Unspecified}
              renderValue={selected =>
                craneOptions
                  .filter(c => selected.includes(c.id))
                  .map(c => c.name)
                  .join(', ')
              }
              multiple
              data-cy='select-cranes-for-split-field'
            >
              {craneOptions.map(crane => (
                <MenuItem
                  key={crane.id}
                  value={crane.id}
                  data-cy={`crane-option-${crane.name.toLowerCase()}`}
                >
                  <Checkbox checked={cranes.indexOf(crane.id) > -1} />
                  <ListItemText primary={crane.name} />
                  <EquipmentStatusChip
                    operationalStatus={crane.operationalStatus}
                    isOwned={crane.isOwned}
                    isInactive={!crane.isActive}
                    isInOperation={equipmentIdsInUse.includes(crane.id)}
                    alignment='end'
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </ConfirmationDialog>
  )
}
